import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router";
import { useSelector } from "react-redux";
import { IoCloseOutline, IoCheckmark } from "react-icons/io5";
import { VscLoading } from "react-icons/vsc";
import axios from "axios";
import Cookies from "js-cookie";
import Modal from "../../modals/Modal";
import ColorBullet from "../ColorBullet";
import "../../../css/notification.css";

const getRelativeTime = (createdAt) => {
    if (!createdAt) return '';
    try {
        const now = new Date();
        const time = new Date(createdAt);
        const diffInSeconds = Math.floor((now - time) / 1000);

        if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
        return `${Math.floor(diffInSeconds / 604800)}w ago`;
    } catch {
        return '';
    }
};

const getPriorityNameFromNotification = (notification) => {
    if (!notification) return null;
    
   
    if (notification.priority_name && typeof notification.priority_name === 'string') {
        return notification.priority_name.toLowerCase();
    }
    
    if (notification.data?.priority_name && typeof notification.data.priority_name === 'string') {
        return notification.data.priority_name.toLowerCase();
    }
    
    if (notification.data?.data?.priority_name && typeof notification.data.data.priority_name === 'string') {
        return notification.data.data.priority_name.toLowerCase();
    }
    
    if (notification?.ticket?.priority?.name) {
        return notification.ticket.priority.name.toLowerCase();
    }
    
    return null;
};

const NotificationCenter = ({ isOpen, hideModal, handleFetchNotificationsCount }) => {
    const [allNotifications, setAllNotifications] = useState([]);
    const [unreadNotifications, setUnreadNotifications] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingReadAll, setIsLoadingReadAll] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalUnread, setTotalUnread] = useState(0);
    const [totalNotifications, setTotalNotifications] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [activeTab, setActiveTab] = useState('unread');
    const [activeFilter, setActiveFilter] = useState(null);
    const [availablePriorities, setAvailablePriorities] = useState([]);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);
    const scrollRef = useRef(null);
    const filtersScrollRef = useRef(null);
    const history = useHistory();
    const userSelector = useSelector(state => state.userReducer);
    const requestTokenRef = useRef(null);




    const fetchNotifications = useCallback(async (page = 1, tab = activeTab) => {
        if (requestTokenRef.current) {
            requestTokenRef.current.cancel('New request initiated');
        }
        requestTokenRef.current = axios.CancelToken.source();

        const workspaceId = userSelector?.user?.current_workspace;
        if (!workspaceId) {
            setIsLoading(false);
            return;
        }

        try {
            const perPage = 12;
            const offset = (page - 1) * perPage;

            const params = new URLSearchParams();
            params.append('offset', offset);
            params.append('limit', perPage);
            params.append('type', tab); 

            const response = await axios.get(`/api/notifications?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${Cookies.get('access_token')}`
                },
                cancelToken: requestTokenRef.current.token
            });

            const data = response.data || {};

            let notificationList = [];
            let paginationInfo = {};

            if (Array.isArray(data.notifications)) {
                notificationList = data.notifications;
                const total = data.pagination?.total || 0;
                const hasMore = data.pagination?.has_more ?? false;

                paginationInfo = {
                    current_page: page,
                    last_page: total > 0 ? Math.ceil(total / perPage) : (hasMore ? page + 1 : page),
                    total: total
                };
            } else if (Array.isArray(data.data)) {
                notificationList = data.data;
                paginationInfo = {
                    current_page: data.current_page || page,
                    last_page: data.last_page || 1,
                    total: data.total || 0
                };
            }

            if (notificationList.length === 0 && data.pagination) {
                paginationInfo = {
                    current_page: page,
                    last_page: data.pagination.last_page || 1,
                    total: data.pagination.total || 0
                };
            }

            if (tab === 'unread') {
                setUnreadNotifications(prev => {
                    if (page === 1) {
                        return notificationList;
                    }
                    const existingMap = new Map(prev.map(n => [n.id, n]));
                    notificationList.forEach(n => existingMap.set(n.id, n));
                    return Array.from(existingMap.values()).sort((a, b) =>
                        new Date(b.created_at) - new Date(a.created_at)
                    );
                });
            } else {
                setAllNotifications(prev => {
                    if (page === 1) {
                        return notificationList;
                    }
                    const existingMap = new Map(prev.map(n => [n.id, n]));
                    notificationList.forEach(n => existingMap.set(n.id, n));
                    return Array.from(existingMap.values()).sort((a, b) =>
                        new Date(b.created_at) - new Date(a.created_at)
                    );
                });
            }

            if (data.total_unread !== undefined) {
                setTotalUnread(data.total_unread);
            }
            if (data.total_all !== undefined) {
                setTotalNotifications(data.total_all);
            }

            setLastPage(paginationInfo.last_page || 1);
            setCurrentPage(paginationInfo.current_page || page);
            setIsLoading(false);
            setIsLoadingMore(false);

        } catch (error) {
            if (axios.isCancel(error)) {
                return;
            }
            console.error('Error fetching notifications:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            setIsLoading(false);
            setIsLoadingMore(false);
        } finally {
            requestTokenRef.current = null;
        }
    }, [userSelector?.user?.current_workspace, activeTab]);

    useEffect(() => {
        if (!userSelector?.user) return;
        if (isOpen) {
            setIsLoading(true);
            setAllNotifications([]);
            setUnreadNotifications([]);
            setCurrentPage(1);
            fetchNotifications(1, activeTab);
        }
    }, [isOpen, userSelector?.user, fetchNotifications, activeTab]);

    useEffect(() => {
        if (!isOpen || !userSelector?.user) return;

        const fetchPriorities = async () => {
            try {
                const response = await axios.get('/api/ticket-priorities', {
                    headers: {
                        Authorization: `Bearer ${Cookies.get('access_token')}`
                    }
                });

                if (Array.isArray(response.data)) {
                    setAvailablePriorities(response.data);
                } else if (response.data?.data && Array.isArray(response.data.data)) {
                    setAvailablePriorities(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching ticket priorities:', error);
                setAvailablePriorities([]);
            }
        };

        fetchPriorities();
    }, [isOpen, userSelector?.user]);

    useEffect(() => {
        const allNotifs = activeTab === 'unread' ? unreadNotifications : allNotifications;
        if (availablePriorities.length === 0 && allNotifs.length > 0) {
            const priorityMap = new Map();
            allNotifs.forEach(n => {
                if (n.ticket?.priority?.id) {
                    const priority = n.ticket.priority;
                    if (!priorityMap.has(priority.id)) {
                        priorityMap.set(priority.id, {
                            id: priority.id,
                            name: priority.name,
                            color: priority.color
                        });
                    }
                }
            });
            const extractedPriorities = Array.from(priorityMap.values());
            if (extractedPriorities.length > 0) {
                setAvailablePriorities(extractedPriorities);
            }
        }
    }, [allNotifications, unreadNotifications, activeTab, availablePriorities.length]);

    useEffect(() => {
        const scrollContainer = filtersScrollRef.current;
        if (!scrollContainer) return;

        const checkScrollPosition = () => {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10); 
        };

        scrollContainer.addEventListener('scroll', checkScrollPosition);
        checkScrollPosition(); 

        return () => {
            scrollContainer.removeEventListener('scroll', checkScrollPosition);
        };
    }, [availablePriorities]);

    const filteredNotifications = useMemo(() => {
        let filtered = activeTab === 'unread' ? unreadNotifications : allNotifications;

        if (activeFilter !== null && activeFilter !== undefined) {
            filtered = filtered.filter(n => {
                const priorityName = getPriorityNameFromNotification(n);
                return priorityName !== null && priorityName === activeFilter;
            });
        }

        return filtered;
    }, [allNotifications, unreadNotifications, activeTab, activeFilter, availablePriorities]);

    const onScroll = useCallback((e) => {
        const el = e.currentTarget;
        if (currentPage >= lastPage || isLoadingMore || isLoading) return;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
            setIsLoadingMore(true);
            fetchNotifications(currentPage + 1, activeTab);
        }
    }, [currentPage, lastPage, isLoadingMore, isLoading, fetchNotifications, activeTab]);

    useEffect(() => {
        if (!userSelector?.user || !isOpen) return;

        const userId = userSelector.user.id;
        const channel = window.Echo?.private(`user.${userId}`);
        if (!channel) return;

        const handler = (data) => {
            if (data.notification) {
                const isUnread = !data.notification.read_at || data.notification.read_at === null || data.notification.read_at === '';
                
                setAllNotifications(prev => {
                    const exists = prev.find(n => n.id === data.notification.id);
                    if (exists) return prev;
                    return [data.notification, ...prev];
                });

                if (isUnread) {
                    setUnreadNotifications(prev => {
                        const exists = prev.find(n => n.id === data.notification.id);
                        if (exists) return prev;
                        return [data.notification, ...prev];
                    });
                    setTotalUnread(prev => prev + 1);
                }

                setTotalNotifications(prev => prev + 1);
            }

            handleFetchNotificationsCount?.();
        };

        channel.listen('.Notification', handler);

        return () => {
            channel.stopListening('.Notification', handler);
        };
    }, [userSelector?.user, isOpen, handleFetchNotificationsCount]);


    const handleClickOnlyBackdrop = (event) => {
        if (event.target.classList.contains('modal-b3')) {
            event.preventDefault();
            event.stopPropagation();
            hideModal();
            handleFetchNotificationsCount?.();
        }
    };

    const scrollFiltersLeft = () => {
        if (filtersScrollRef.current) {
            filtersScrollRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    const scrollFiltersRight = () => {
        if (filtersScrollRef.current) {
            filtersScrollRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    const markAllRead = async (e) => {
        e.stopPropagation();
        setIsLoadingReadAll(true);
        try {
            await axios.get('/api/notifications/mark-read', {
                headers: { Authorization: `Bearer ${Cookies.get('access_token')}` }
            });
            const readTimestamp = new Date().toISOString();
            setAllNotifications(prev => prev.map(n => ({ ...n, read_at: readTimestamp })));
            setUnreadNotifications([]);
            setTotalUnread(0);
            handleFetchNotificationsCount?.();
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        } finally {
            setIsLoadingReadAll(false);
        }
    };

    return (
        isOpen && (
            <Modal>
                <div onClick={handleClickOnlyBackdrop} className="notification-wrapper modal d-block modal-b3">
                    <div className="notification-center-modal">
                        <div className="notification-header d-flex justify-content-between align-items-center">
                            <h6>Notifications</h6>
                            <IoCloseOutline
                                onClick={hideModal}
                                className="notification-close-icon"
                                size={24}
                            />
                        </div>
                        <div className="notification-header-top">
                            <div className="notification-tabs">
                                <button
                                    className={`${activeTab === 'unread' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('unread');
                                        setIsLoading(true);
                                        setCurrentPage(1);
                                        fetchNotifications(1, 'unread');
                                    }}
                                >
                                    Unread
                                    <span className="notification-badge-count">{totalUnread}</span>
                                </button>
                                <button
                                    className={`${activeTab === 'all' ? 'active' : ''}`}
                                    onClick={() => {
                                        setActiveTab('all');
                                        setIsLoading(true);
                                        setCurrentPage(1);
                                        fetchNotifications(1, 'all');
                                    }}
                                >
                                    All
                                    {totalNotifications > 0 && <span className="notification-badge-all">{totalNotifications}</span>}
                                </button>
                            </div>

                        </div>

                        <div className="notification-filters w-100">
                            <div 
                                className={`filter-arrow left ${!canScrollLeft ? 'disabled' : ''}`}
                                onClick={canScrollLeft ? scrollFiltersLeft : undefined}
                            >
                                &lt;
                            </div>
                            <div 
                                className="notification-filters-scroll"
                                ref={filtersScrollRef}
                            >
                                <button
                                    className={`filter-pill ${activeFilter === null ? 'active' : ''}`}
                                    onClick={() => setActiveFilter(null)}
                                >
                                    All
                                </button>
                               
                                {availablePriorities.map(priority => {
                                    const priorityNameLower = priority.name.toLowerCase();
                                    return (
                                        <button
                                            key={priority.id}
                                            className={`filter-pill priority-${priorityNameLower.replace(/\s+/g, '-')} ${activeFilter === priorityNameLower ? 'active' : ''}`}
                                            onClick={() => setActiveFilter(activeFilter === priorityNameLower ? null : priorityNameLower)}
                                            style={{
                                                borderColor: priority.color || '#3b82f6',
                                                backgroundColor: activeFilter === priorityNameLower ? (priority.color || '#3b82f6') : undefined,
                                                color: activeFilter === priorityNameLower ? 'white' : priority.color,
                                            }}
                                        >
                                            {priority.name}
                                        </button>
                                    );
                                })}
                            </div>
                            <div 
                                className={`filter-arrow right ms-auto ${!canScrollRight ? 'disabled' : ''}`}
                                onClick={canScrollRight ? scrollFiltersRight : undefined}
                            >
                                &gt;
                            </div>
                        </div>

                        <div
                            className="notification-list"
                            ref={scrollRef}
                            onScroll={onScroll}
                        >
                            {isLoading && (
                                <div className="notification-loading">
                                    <VscLoading className="spin-loading" size={24} />
                                    <div>Loading...</div>
                                </div>
                            )}

                            {!isLoading && filteredNotifications.length === 0 && (
                                <div className="notification-empty">
                                    No notifications
                                </div>
                            )}

                            {filteredNotifications.map((notification) => {
                                const isUnread = !notification.read_at || notification.read_at === null || notification.read_at === '';
                                
                                return (
                                <NotificationItem
                                    key={notification.id}
                                    notification={notification}
                                    history={history}
                                    setAllNotifications={setAllNotifications}
                                    setUnreadNotifications={setUnreadNotifications}
                                    setTotalUnread={setTotalUnread}
                                    hideModal={hideModal}
                                    isUnread={isUnread}
                                    availablePriorities={availablePriorities}
                                />
                                );
                            })}

                            {isLoadingMore && (
                                <div className="notification-loading-more" style={{ textAlign: 'center', padding: '10px' }}>
                                    <VscLoading className="spin-loading" size={20} />
                                </div>
                            )}
                        </div>

                        <div className="notification-footer">
                            <button
                                className="mark-read-btn"
                                onClick={markAllRead}
                                disabled={isLoadingReadAll || totalUnread === 0}
                            >
                                {isLoadingReadAll ? (
                                    <>
                                        <VscLoading className="spin-loading" size={16} />
                                        <span>Marking...</span>
                                    </>
                                ) : (
                                    <>
                                        <IoCheckmark size={18} className="check-icon" />
                                        <span>Mark all as Read</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        )
    );
};

function NotificationItem({ notification, history, setAllNotifications, setUnreadNotifications, setTotalUnread, hideModal, isUnread: isUnreadProp, availablePriorities = [] }) {
    const isUnread = isUnreadProp !== undefined ? isUnreadProp : (!notification.read_at || notification.read_at === null || notification.read_at === '');
    const userName = notification?.user?.name ?? notification?.user?.email ?? 'System';
    const ticketTitle = notification?.ticket?.title ?? 'Untitled Ticket';
    const ticketId = notification?.ticket?.id;
    const ticketType = notification?.ticket?.type;
    
    let ticketPriority = notification?.ticket?.priority;
    
    const priorityName = getPriorityNameFromNotification(notification);
    
    if (!ticketPriority && priorityName && availablePriorities.length > 0) {
        ticketPriority = availablePriorities.find(p => p.name.toLowerCase() === priorityName) || null;
    }

    const statusDotColor = ticketPriority?.color || '#0ea5e9';

    const getNotificationContent = () => {
        let title = ticketTitle;
        let subtext = '';

        switch (notification.type) {
            case 'new-ticket':
                if (ticketTitle === 'Untitled Ticket' && ticketType?.name) {
                    title = ticketType.name;
                } else {
                    title = ticketTitle;
                }
                subtext = `${userName} created a new ticket`;
                break;
            case 'status-updated':
                title = ticketTitle;
                subtext = `${userName} updated status to "${notification.new_status || 'N/A'}"`;
                break;
            case 'assignees-updated':
                const addedCount = Array.isArray(notification.added) ? notification.added.length : 0;
                title = ticketTitle;
                subtext = `${userName} ${addedCount > 0 ? `added ${addedCount} assignee(s)` : 'updated assignees'}`;
                break;
            case 'reply-created':
            case 'note-created':
                const preview = notification.comment_preview || '';
                const truncatedPreview = preview.length > 70 ? preview.substring(0, 67) + '...' : preview;
                title = ticketTitle;
                subtext = `${userName}: ${truncatedPreview}`;
                break;
            default:
                title = ticketTitle;
                subtext = 'New notification';
        }
        return { title, subtext };
    };

    const content = getNotificationContent();

    const handleClick = () => {
        if (isUnread) {
            axios.get(`/api/notifications/mark-read?id=${notification.id}`, {
                headers: { Authorization: `Bearer ${Cookies.get('access_token')}` }
            }).then(() => {
                const readTimestamp = new Date().toISOString();
                setAllNotifications(prev => prev.map(n =>
                    n.id === notification.id ? { ...n, read_at: readTimestamp } : n
                ));
                setUnreadNotifications(prev => prev.filter(n => n.id !== notification.id));
                setTotalUnread(prev => Math.max(0, prev - 1));
            });
        }

        hideModal?.();

        const url = notification?.url || (ticketId ? `/ticket/${ticketId}` : null);
        if (url) {
            try {
                if (/^https?:\/\//i.test(url)) {
                    window.location.assign(url);
                } else {
                    history.push(url);
                }
            } catch (e) {
                console.error('Error navigating to notification URL:', e);
            }
        }
    };

    return (
        <div
            className={`notification-item ${isUnread ? 'unread' : ''} ${priorityName ? `priority-${priorityName.replace(/\s+/g, '-')}` : ''}`}
            onClick={handleClick}
        >
            <div className="notification-icon-col">
                <div
                    className="icon-circle"
                    style={{
                        backgroundColor: statusDotColor,
                        color: statusDotColor
                    }}
                />
            </div>
            <div className="notification-content-col">
                <div className="notification-row-top">
                    <div className="notification-title">
                        {content.title}
                    </div>
                    <div className="notification-time">
                        {getRelativeTime(notification.created_at)}
                    </div>
                </div>
                <div className="notification-body">
                    <div style={{ marginBottom: '4px' }}>
                        {content.subtext}
                    </div>
                </div>
            </div>
        </div>
    );
}


export default NotificationCenter;

